import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import HighlightEditor from './HighlightEditor';
import { updateZettel, triggerReadwiseSync, fetchSyncStatus, fetchHighlightZettelMap, deleteReadwiseHighlight, updateReadwiseHighlight, fetchHighlightStatus, setHighlightStatus as apiSetHighlightStatus, createCustomSource, addCustomHighlight, updateCustomHighlight, deleteCustomHighlight, deleteCustomSource, fetchReadwiseSource } from '../api';
const APP_VERSION = require('../../package.json').version; // eslint-disable-line

const COLOR_MAP = {
  yellow: '#ffc107', blue: '#2196f3', red: '#e94560', orange: '#ff9800', pink: '#e91e63',
};

// Shared icons — identical to ZettelTree.js InsertPopover
const InsertIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);
const ChildIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 3v5.5a2 2 0 0 0 2 2h4" /><polyline points="7 8.5 9 10.5 7 12.5" />
  </svg>
);
const BranchIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="2" y1="7" x2="12" y2="7" /><polyline points="4 5 2 7 4 9" /><polyline points="10 5 12 7 10 9" />
  </svg>
);

const CATEGORY_LABELS = {
  articles: 'Artikel',
  books: 'Bücher',
  podcasts: 'Podcasts',
  tweets: 'Tweets',
  videos: 'Videos',
  kurse: 'Kurse',
  vorträge: 'Vorträge',
  gespräche: 'Gespräche',
  supplementals: 'Sonstiges',
};

// Normalisiert Singular-/Tippfehler-Varianten auf den kanonischen CATEGORY_LABELS-Key
const CATEGORY_ALIASES = {
  kurs: 'kurse', vortrag: 'vorträge', gespräch: 'gespräche', gespraech: 'gespräche',
  article: 'articles', book: 'books', podcast: 'podcasts', tweet: 'tweets',
  video: 'videos',
  buch: 'books', bücher: 'books', artikel: 'articles',
};

// Kategorie-Label holen: erst Alias prüfen, dann CATEGORY_LABELS, dann capitalize als Fallback
function getCategoryLabel(cat) {
  const normalized = CATEGORY_ALIASES[cat] || cat;
  if (CATEGORY_LABELS[normalized]) return CATEGORY_LABELS[normalized];
  // Fallback: erster Buchstabe groß
  return cat.charAt(0).toUpperCase() + cat.slice(1);
}

// Vorschlagsliste für Kategorie-Auswahl bei eigenen Quellen
const CUSTOM_CATEGORY_SUGGESTIONS = [
  'books', 'articles', 'podcasts', 'kurse', 'vorträge', 'gespräche',
];

// Stift-Icon für eigene Quellen (visueller Marker)
const CustomSourceIcon = () => (
  <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11.5 1.5l3 3L5 14H2v-3L11.5 1.5z" />
  </svg>
);

function renderInlineMarkdown(line, keyPrefix) {
  const parts = [];
  let remaining = line;
  let partKey = 0;
  while (remaining.length > 0) {
    const hlMatch = remaining.match(/^(.*?)==([^=]+)==(.*)$/);
    const boldMatch = remaining.match(/^(.*?)\*\*([^*]+)\*\*(.*)$/);
    const italicMatch = remaining.match(/^(.*?)(?<!\*)\*([^*]+)\*(?!\*)(.*)$/);
    let earliest = null;
    let type = null;
    if (hlMatch && (!earliest || hlMatch[1].length < earliest[1].length)) { earliest = hlMatch; type = 'hl'; }
    if (boldMatch && (!earliest || boldMatch[1].length < earliest[1].length)) { earliest = boldMatch; type = 'bold'; }
    if (italicMatch && (!earliest || italicMatch[1].length < earliest[1].length)) { earliest = italicMatch; type = 'italic'; }
    if (!earliest) { parts.push(remaining); break; }
    if (earliest[1]) parts.push(earliest[1]);
    if (type === 'hl') parts.push(<mark key={`${keyPrefix}-${partKey++}`} className="hl-inline-mark">{earliest[2]}</mark>);
    else if (type === 'bold') parts.push(<strong key={`${keyPrefix}-${partKey++}`}>{earliest[2]}</strong>);
    else if (type === 'italic') parts.push(<em key={`${keyPrefix}-${partKey++}`}>{earliest[2]}</em>);
    remaining = earliest[3];
  }
  return parts;
}

function renderHighlightText(text) {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    // Bild
    const imgMatch = line.match(/^!\[([^\]]*)\]\(([^)]+)\)$/);
    if (imgMatch) {
      const imgSrc = /^https?:\/\//i.test(imgMatch[2]) ? imgMatch[2] : null;
      if (imgSrc) elements.push(<img key={i} src={imgSrc} alt={imgMatch[1]} className="hl-inline-image" />);
      continue;
    }

    // Blockquote: > text
    if (line.startsWith('> ')) {
      elements.push(
        <div key={i} className="hl-preview-blockquote">
          {renderInlineMarkdown(line.slice(2), i)}
        </div>
      );
      continue;
    }

    // Unordered List: - item oder * item
    const ulMatch = line.match(/^(\s*)([-*])\s+(.*)/);
    if (ulMatch) {
      const depth = Math.floor(ulMatch[1].length / 2);
      elements.push(
        <div key={i} className="hl-preview-ul" style={{ paddingLeft: (depth * 20) + 8 }}>
          <span className="hl-preview-bullet">{depth === 0 ? '•' : '◦'}</span>
          {renderInlineMarkdown(ulMatch[3], i)}
        </div>
      );
      continue;
    }

    // Ordered List: 1. item
    const olMatch = line.match(/^(\s*)(\d+)\.\s+(.*)/);
    if (olMatch) {
      const depth = Math.floor(olMatch[1].length / 2);
      elements.push(
        <div key={i} className="hl-preview-ol" style={{ paddingLeft: (depth * 20) + 8 }}>
          <span className="hl-preview-num">{olMatch[2]}.</span>
          {renderInlineMarkdown(olMatch[3], i)}
        </div>
      );
      continue;
    }

    // Leerzeile
    if (!line.trim()) {
      elements.push(<div key={i} className="hl-preview-spacer" />);
      continue;
    }

    // Normale Zeile mit Inline-Markdown
    elements.push(<span key={i}>{renderInlineMarkdown(line, i)}{i < lines.length - 1 && <br />}</span>);
  }

  return elements;
}

// Formatierter Text für Edit-Overlay (HTML mit Markdown-Vorschau)
function renderEditOverlayHtml(text) {
  if (!text) return '';
  let html = text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/==([^=]+)==/g, '<mark class="hl-overlay-mark">$1</mark>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(?<!\*)\*([^*]+)\*(?!\*)/g, '<em>$1</em>')
    .replace(/\n/g, '<br>');
  return html;
}

// SVG icons for triage — stroke-based, consistent with app icon language
const IconOffen = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <circle cx="6" cy="6" r="4.5" />
  </svg>
);
const IconGemerkt = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M2.5 2h7v8.5l-3.5-2-3.5 2V2z" />
  </svg>
);
const IconArchiviert = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="2.5 6.5 5 9 10 3" />
  </svg>
);
const IconGeloescht = () => (
  <svg width="13" height="13" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="6" cy="6" r="4.5" opacity="0.4" />
    <line x1="4" y1="4" x2="8" y2="8" opacity="0.4" />
    <line x1="8" y1="4" x2="4" y2="8" opacity="0.4" />
  </svg>
);

const STATUS_CONFIG = {
  offen:       { label: 'Offen',       Icon: IconOffen,      cls: 'status-offen' },
  gemerkt:     { label: 'Gemerkt',     Icon: IconGemerkt,    cls: 'status-gemerkt' },
  archiviert:  { label: 'Erledigt',    Icon: IconArchiviert, cls: 'status-archiviert' },
  'gelöscht':  { label: 'Ausgeblendet', Icon: IconGeloescht,  cls: 'status-geloescht' },
};
const STATUS_ORDER = ['offen', 'gemerkt', 'archiviert'];

// PERF: React.memo verhindert Re-Renders von Karten, die sich nicht geändert haben
// Ohne das: Jeder State-Change im ReadwisePanel rendert ALLE 250+ HighlightCards neu
const HighlightCard = React.memo(function HighlightCard({ highlight, source, zettelIds, onOpenZettel, isTarget, onLinkHighlight, onDelete, onUnlink, onEdit, status = 'offen', onStatusChange, sourceKey, isKbFocused, kbAction, onKbActionHandled, onEditingChange, onHighlightPreview }) {
  const normalize = (v) => Array.isArray(v) ? v : (v ? [v] : []);
  const [removedIds, setRemovedIds] = useState([]);
  const [showMenu, setShowMenu] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [copyFlash, setCopyFlash] = useState(false);
  const [showCopiedBadge, setShowCopiedBadge] = useState(false);
  const copiedBadgeTimer = useRef(null);
  const [savedToast, setSavedToast] = useState(false);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const unsavedTextRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const cardRef = useRef(null);
  const menuRef = useRef(null);
  const btnRef = useRef(null);
  const statusBtnRef = useRef(null);
  const statusMenuRef = useRef(null);
  const contentRef = useRef(null);
  const savedToastTimerRef = useRef(null);

  // --- Drag & Drop: Highlight als kompakte Vorschau-Karte ziehen ---
  const ghostTimeoutRef = useRef(null);
  const ghostWrapperRef = useRef(null);

  const handleDragStart = useCallback((e) => {
    const data = {
      ref: highlight.ref,
      text: highlight.text,
      author: source.author,
      title: source.title,
    };
    e.dataTransfer.setData('application/highlight-ref', JSON.stringify(data));
    e.dataTransfer.effectAllowed = 'copyMove';
    setIsDragging(true);

    // Kompakte Ghost-Karte erstellen (einheitliche Größe, max 2 Zeilen Text)
    // Wrapper nötig: Browser schneidet setDragImage am Bounding-Box ab,
    // daher muss der Wrapper groß genug sein, damit die Rotation sichtbar bleibt.
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:fixed;top:-500px;left:-500px;width:260px;height:90px;z-index:99999;pointer-events:none;';
    const ghost = document.createElement('div');
    ghost.className = 'drag-ghost-card';
    const lines = (highlight.text || '').split('\n').slice(0, 2);
    const preview = lines.map(l => l.length > 60 ? l.slice(0, 60) + '…' : l).join('\n');
    ghost.innerHTML = `<span class="drag-ghost-icon">📎</span><span class="drag-ghost-text">${preview.replace(/</g, '&lt;').replace(/\n/g, '<br>')}</span>`;
    ghost.style.cssText = 'width:220px;max-height:56px;overflow:hidden;padding:8px 12px;background:var(--color-bg-elevated,#fff);border:1.5px solid var(--color-primary,#e94560);border-radius:8px;font-size:11px;line-height:1.4;color:var(--color-text-secondary,#555);display:flex;align-items:flex-start;gap:8px;box-shadow:0 4px 16px rgba(0,0,0,.15);pointer-events:none;transform:rotate(3deg);transform-origin:top left;margin:4px;';
    wrapper.appendChild(ghost);
    document.body.appendChild(wrapper);
    ghostWrapperRef.current = wrapper;
    e.dataTransfer.setDragImage(wrapper, 16, 10);
    // MAYA-2: Ghost nach 300ms entfernen (wie LiveEditor CRITICAL-2 — 100ms zu kurz auf langsamen Systemen).
    // Wrapper direkt referenziert statt über Ref → kein Überschreiben bei Schnell-Drag.
    if (ghostTimeoutRef.current) clearTimeout(ghostTimeoutRef.current);
    const wrapperToClean = wrapper;
    ghostTimeoutRef.current = setTimeout(() => {
      try { wrapperToClean.parentNode?.removeChild(wrapperToClean); } catch (_) {}
      ghostTimeoutRef.current = null;
      if (ghostWrapperRef.current === wrapperToClean) ghostWrapperRef.current = null;
    }, 300);
  }, [highlight, source]);

  const handleDragEnd = useCallback(() => {
    setIsDragging(false);
    if (ghostTimeoutRef.current) {
      clearTimeout(ghostTimeoutRef.current);
      ghostTimeoutRef.current = null;
    }
    // Fix: Ghost-Element IMMER entfernen (auch bei ESC-Abbruch)
    if (ghostWrapperRef.current && ghostWrapperRef.current.parentNode) {
      ghostWrapperRef.current.parentNode.removeChild(ghostWrapperRef.current);
      ghostWrapperRef.current = null;
    }
  }, []);

  // Cleanup Ghost-Timeout + Ghost-Element bei Unmount
  useEffect(() => {
    return () => {
      if (ghostTimeoutRef.current) clearTimeout(ghostTimeoutRef.current);
      if (ghostWrapperRef.current && ghostWrapperRef.current.parentNode) {
        ghostWrapperRef.current.parentNode.removeChild(ghostWrapperRef.current);
      }
    };
  }, []);

  // Cleanup savedToast-Timer bei Unmount
  useEffect(() => {
    return () => {
      if (savedToastTimerRef.current) clearTimeout(savedToastTimerRef.current);
    };
  }, []);

  // Close menus on click outside (konsolidiert)
  useEffect(() => {
    if (!showMenu && !showStatusMenu) return;
    const handleClickOutside = (e) => {
      if (showMenu && menuRef.current && !menuRef.current.contains(e.target) &&
          btnRef.current && !btnRef.current.contains(e.target)) {
        setShowMenu(false);
        setMenuAnchor(null);
      }
      if (showStatusMenu && statusMenuRef.current && !statusMenuRef.current.contains(e.target) &&
          statusBtnRef.current && !statusBtnRef.current.contains(e.target)) {
        setShowStatusMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMenu, showStatusMenu]);

  // Wenn eine zuvor entfernte ID wieder in zettelIds auftaucht (z.B. nach erneutem Drop),
  // aus removedIds entfernen, damit der Link-Chip wieder angezeigt wird.
  useEffect(() => {
    if (removedIds.length === 0) return;
    const current = normalize(zettelIds);
    const stillRemoved = removedIds.filter(id => !current.includes(id));
    if (stillRemoved.length !== removedIds.length) {
      setRemovedIds(stillRemoved);
    }
  }, [zettelIds]); // removedIds absichtlich ausgelassen (Endlos-Loop)

  // Filter out optimistically removed IDs
  const linkedIds = normalize(zettelIds).filter(id => !removedIds.includes(id));

  useEffect(() => {
    if (isTarget && cardRef.current) {
      setTimeout(() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
    }
  }, [isTarget]);

  // Overflow detection: check if highlight content exceeds the collapsed max-height
  const HL_MAX_HEIGHT = 120;
  useEffect(() => {
    if (!contentRef.current || editing) return;
    // scrollHeight is always the natural full height regardless of max-height constraint
    const check = () => {
      if (contentRef.current) {
        setIsOverflowing(contentRef.current.scrollHeight > HL_MAX_HEIGHT + 4);
      }
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(contentRef.current);
    return () => ro.disconnect();
  }, [highlight.text, highlight.note, editing]);

  // Explizites Speichern (⌘↵ oder Button)
  const handleSaveExplicit = useCallback((newText) => {
    if (newText === editText && !unsavedChanges) return;
    setSaving(true);
    onEdit(highlight, newText, editNote)
      .then(() => {
        setEditText(newText);
        setUnsavedChanges(false);
        unsavedTextRef.current = null;
        clearTimeout(savedToastTimerRef.current);
        setSavedToast(true);
        savedToastTimerRef.current = setTimeout(() => setSavedToast(false), 2000);
      })
      .catch(() => { /* intentional: error handled in onEdit */ })
      .finally(() => setSaving(false));
  }, [editText, editNote, highlight, onEdit, unsavedChanges]);

  // Echtzeit-Preview → aktualisiert eingebettetes Highlight im Zettel-Editor
  const handlePreview = useCallback((newText) => {
    unsavedTextRef.current = newText;
    if (onHighlightPreview) onHighlightPreview(highlight.ref, newText);
  }, [highlight.ref, onHighlightPreview]);

  // Blur mit ungespeicherten Änderungen → Inline-Leiste zeigen
  const handleBlurWithChanges = useCallback(() => {
    setUnsavedChanges(true);
  }, []);

  const handleDiscard = useCallback((originalText) => {
    if (originalText != null) {
      setEditText(originalText);
      // Revert Preview im Zettel-Editor
      if (onHighlightPreview) onHighlightPreview(highlight.ref, originalText);
    }
    setEditing(false);
    setUnsavedChanges(false);
    unsavedTextRef.current = null;
    // Fokus zurück auf Sidebar, damit Keyboard-Navigation weitergeht
    setTimeout(() => {
      const sidebar = document.querySelector('.panel-sidebar');
      if (sidebar) sidebar.focus();
    }, 0);
  }, [highlight.ref, onHighlightPreview]);

  // After expand animation finishes, remove max-height so content can breathe
  useEffect(() => {
    const el = contentRef.current;
    if (!el || !expanded) return;
    const handler = () => { el.style.maxHeight = 'none'; };
    el.addEventListener('transitionend', handler);
    return () => el.removeEventListener('transitionend', handler);
  }, [expanded]);

  // Click-outside: Karte zusammenklappen + Editiermodus verlassen
  useEffect(() => {
    if (!expanded && !editing) return;
    const handlePointerDown = (e) => {
      if (cardRef.current && !cardRef.current.contains(e.target)) {
        setExpanded(false);
        setEditing(false);
      }
    };
    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => document.removeEventListener('pointerdown', handlePointerDown, true);
  }, [expanded, editing]);

  // Edit-Status an Parent melden
  useEffect(() => {
    if (onEditingChange) onEditingChange(editing);
  }, [editing]); // eslint-disable-line

  // Tastatur-Aktionen von ReadwisePanel (Bibliothek-Keyboard-Nav)
  useEffect(() => {
    if (!kbAction || !isKbFocused) return;
    if (kbAction === 'open') {
      setExpanded(true);
    } else if (kbAction === 'close') {
      setExpanded(false);
    } else if (kbAction === 'expand') {
      setExpanded(prev => !prev);
    } else if (kbAction === 'edit') {
      setExpanded(true);
      setEditing(true);
      setEditText(highlight.text || '');
      setEditNote(highlight.note || '');
    } else if (kbAction === 'link') {
      if (onLinkHighlight) onLinkHighlight(highlight);
    } else if (kbAction === 'copy') {
      setCopyFlash(true);
      setTimeout(() => setCopyFlash(false), 380);
      setShowCopiedBadge(true);
      clearTimeout(copiedBadgeTimer.current);
      copiedBadgeTimer.current = setTimeout(() => setShowCopiedBadge(false), 3000);
    }
    if (onKbActionHandled) onKbActionHandled();
  }, [kbAction]); // eslint-disable-line

  const colorBar = highlight.color && COLOR_MAP[highlight.color]
    ? <div className="hl-color-stripe" style={{ background: COLOR_MAP[highlight.color] }} />
    : null;

  const statusCfg = STATUS_CONFIG[status] || STATUS_CONFIG.offen;
  const StatusIcon = statusCfg.Icon;

  const handleStatusClick = useCallback((e) => {
    if (e.shiftKey) {
      const cycle = { offen: 'gemerkt', gemerkt: 'archiviert', archiviert: 'offen' };
      const current = status || 'offen';
      onStatusChange && onStatusChange(highlight.ref, cycle[current]);
      e.stopPropagation();
      return;
    }
    setShowStatusMenu(s => !s);
  }, [status, highlight.ref, onStatusChange]);

  const handleStatusKeyDown = useCallback((e) => {
    const keyMap = { '1': 'offen', '2': 'gemerkt', '3': 'archiviert' };
    if (keyMap[e.key]) {
      onStatusChange && onStatusChange(highlight.ref, keyMap[e.key]);
      e.preventDefault();
    }
  }, [highlight.ref, onStatusChange]);

  return (
    <div ref={cardRef} className={`highlight-card-wrapper${isTarget ? ' targeted' : ''} ${status !== 'offen' ? `hl-card-${status}` : ''}${isDragging ? ' hl-dragging' : ''}${isKbFocused ? ' hl-card-kb-focused' : ''}${copyFlash ? ' hl-copy-flash' : ''}`}
      draggable={!editing}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      title={!editing ? 'In Zettelkasten oder Notiz-Editor ziehen zum Verknüpfen' : undefined}
    >
      {showCopiedBadge && <span className="hl-copied-badge">Kopiert</span>}
      <div className="hl-card-body">
        {colorBar}
        <div className="hl-card-body-content">
          {editing ? (
            <div className="hl-edit-form">
              <HighlightEditor
                value={editText}
                onSave={handleSaveExplicit}
                onDiscard={handleDiscard}
                onPreview={handlePreview}
                onBlurWithChanges={handleBlurWithChanges}
              />
              <div className="hl-edit-status-row">
                {saving
                  ? <span className="hl-save-indicator">Speichert…</span>
                  : savedToast
                    ? <span className="hl-saved-toast">Gespeichert ✓</span>
                    : unsavedChanges
                      ? <div className="hl-unsaved-bar">
                          <span className="hl-unsaved-label">Ungespeicherte Änderungen</span>
                          <button className="hl-unsaved-btn hl-unsaved-save" onClick={() => handleSaveExplicit(unsavedTextRef.current || editText)}>Speichern ⌘↵</button>
                          <button className="hl-unsaved-btn hl-unsaved-discard" onClick={() => handleDiscard(highlight.text)}>Verwerfen</button>
                        </div>
                      : <span className="hl-edit-hint">⌘↵ Speichern · Esc Verwerfen</span>
                }
              </div>
            </div>
          ) : (
            <>
              <div
                ref={contentRef}
                className="hl-expand-content"
                style={{ maxHeight: expanded ? undefined : HL_MAX_HEIGHT }}
              >
                <blockquote
                  style={{
                    borderLeft: '3px solid color-mix(in srgb, var(--color-primary) 40%, transparent)',
                    paddingLeft: 12, margin: '0 0 10px 0',
                    color: 'var(--color-text-secondary)', fontStyle: 'italic', lineHeight: 1.65,
                    background: 'transparent', borderRadius: 0, padding: '0 0 0 12px',
                    fontSize: 'var(--text-sm)', cursor: 'pointer'
                  }}
                  onClick={() => { setEditText(highlight.text); setEditNote(highlight.note || ''); setEditing(true); }}
                  title="Klicken zum Bearbeiten"
                >
                  {renderHighlightText(highlight.text)}
                </blockquote>

                {highlight.note && (
                  <p
                    style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--text-sm)', margin: '0 0 6px 0', cursor: 'pointer' }}
                    onClick={() => { setEditText(highlight.text); setEditNote(highlight.note || ''); setEditing(true); }}
                    title="Klicken zum Bearbeiten"
                  >
                    <strong>Notiz:</strong> {highlight.note}
                  </p>
                )}

                {/* Gradient fade — only when collapsed and overflowing */}
                {!expanded && isOverflowing && (
                  <div className="hl-expand-gradient" />
                )}
              </div>

              {/* Mehr/Weniger anzeigen button */}
              {isOverflowing && (
                <button
                  className={`hl-expand-btn${expanded ? ' is-expanded' : ''}`}
                  onClick={() => {
                    const el = contentRef.current;
                    if (!el) { setExpanded(e => !e); return; }
                    if (expanded) {
                      // Collapse: set current scrollHeight first, then animate to HL_MAX_HEIGHT
                      el.style.maxHeight = el.scrollHeight + 'px';
                      requestAnimationFrame(() => {
                        el.style.maxHeight = HL_MAX_HEIGHT + 'px';
                      });
                      setExpanded(false);
                    } else {
                      // Expand: measure scrollHeight and animate to it
                      el.style.maxHeight = el.scrollHeight + 'px';
                      setExpanded(true);
                    }
                  }}
                >
                  {expanded ? 'Weniger anzeigen' : 'Mehr anzeigen'}
                </button>
              )}
            </>
          )}

          {highlight.tags?.length > 0 && (
            <div className="hl-tags-row">
              {highlight.tags.map(t => <span key={t} className="tag">#{t}</span>)}
            </div>
          )}

          {/* Footer: Meta-Infos, optional Zeichenzähler (mittig), Zettel-Links, Status, Trash */}
          <div className="hl-footer">
            {highlight.location && <span className="hl-meta-text">Pos. {highlight.location}</span>}
            {highlight.highlighted_at && <span className="hl-meta-text">{new Date(highlight.highlighted_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>}
            {editing && (
              <span className="hl-edit-count-footer">
                {editText.length} Zeichen · {editText.trim() ? editText.trim().split(/\s+/).length : 0} Wörter
              </span>
            )}
            <div className="flex-spacer" />
            {linkedIds.map(id => (
              <button key={id} className="zettel-link-chip" onClick={() => onOpenZettel(id)}>
                <span className="zettel-link-chip-label">Zettel {id}</span>
                <span
                  className="zettel-link-chip-unlink"
                  role="button"
                  aria-label="Verknüpfung aufheben"
                  title="Verknüpfung aufheben"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (window.confirm(`Verknüpfung zu Zettel ${id} aufheben?`)) {
                      setRemovedIds(prev => [...prev, id]);
                      if (onUnlink) onUnlink(id, highlight.ref);
                    }
                  }}
                >✕</span>
              </button>
            ))}
            {/* Status + Trash — inline im Footer, nicht über dem Text */}
            <div className="hl-status-badge-wrap">
              {status === 'gelöscht' ? (
                <button
                  className="hl-restore-btn"
                  aria-label="Highlight einblenden"
                  onClick={() => onStatusChange && onStatusChange(highlight.ref, 'offen')}
                  title="Einblenden"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                </button>
              ) : (
                <button
                  className="hl-trash-btn"
                  aria-label="Highlight ausblenden"
                  onClick={() => onDelete && onDelete(highlight)}
                  title="Ausblenden"
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                    <line x1="1" y1="1" x2="23" y2="23"/>
                  </svg>
                </button>
              )}
              <button
                ref={statusBtnRef}
                className={`hl-status-badge ${statusCfg.cls}`}
                aria-label={`Status: ${statusCfg.label}`}
                onClick={handleStatusClick}
                onKeyDown={handleStatusKeyDown}
                title={`Status: ${statusCfg.label} — klicken zum Ändern, Shift+Klick zum Wechseln, 1/2/3 bei Fokus`}
              >
                <span className="hl-status-icon"><StatusIcon /></span>
                <span className="hl-status-label">{statusCfg.label}</span>
              </button>
              {showStatusMenu && ReactDOM.createPortal(
                <div
                  ref={statusMenuRef}
                  className="hl-status-menu"
                  style={{
                    position: 'fixed',
                    right: window.innerWidth - (statusBtnRef.current?.getBoundingClientRect().right || 0),
                    top: (statusBtnRef.current?.getBoundingClientRect().bottom || 0) + 6,
                    zIndex: 9999,
                  }}
                >
                  {STATUS_ORDER.map(s => {
                    const MenuIcon = STATUS_CONFIG[s].Icon;
                    return (
                      <button
                        key={s}
                        className={`hl-status-menu-item ${STATUS_CONFIG[s].cls}${s === status ? ' active' : ''}`}
                        onClick={() => { onStatusChange && onStatusChange(highlight.ref, s); setShowStatusMenu(false); }}
                      >
                        <span className="hl-status-icon"><MenuIcon /></span>
                        <span>{STATUS_CONFIG[s].label}</span>
                        {s === status && (
                          <span className="hl-status-check">
                            <svg width="10" height="10" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="1.5 5.2 3.8 7.5 8.5 2.5" />
                            </svg>
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>,
                document.body
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

function ReadwisePanel({ onOpenZettel, initialHighlightRef, onHighlightRefUsed, initialSourceId, onSourceSelected, onLinkHighlight, refreshKey, resetKey, onOpenCmd, onHighlightEdited, onUnlinkFromNote, sources: sourcesProp, onRefreshSources, initialHighlightZettelMap, initialHighlightStatus, initialSyncing, highlightUnlinkEvent, highlightLinkEvent, focusedPanel, onShowShortcutTip, onDetailChange, onHighlightFocusChange, onHighlightEditingChange, onHighlightCopied, readOnly = false }) {
  // Sources come from App.js — no independent fetch needed
  const sources = sourcesProp || [];
  const [selectedSource, setSelectedSource] = useState(null);
  const [targetHighlightRef, setTargetHighlightRef] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState('');
  const [filter, setFilter] = useState('all');
  const [highlightZettelMap, setHighlightZettelMap] = useState(initialHighlightZettelMap || {});
  const [searchQuery, setSearchQuery] = useState('');
  const [highlightStatus, setHighlightStatus] = useState(initialHighlightStatus || {}); // ref → 'offen'|'gemerkt'|'archiviert'
  const [triageFilter, setTriageFilter] = useState('offen'); // 'offen' | 'gemerkt' | 'alle'
  const pollRef = useRef(null);
  const isMountedRef = useRef(true);

  // Cleanup isMountedRef bei Unmount
  useEffect(() => {
    return () => { isMountedRef.current = false; };
  }, []);

  // Report detail view state to parent for hint bar
  useEffect(() => {
    if (onDetailChange) onDetailChange(!!selectedSource);
  }, [selectedSource, onDetailChange]);

  // Custom source creation state
  const [showCreateSource, setShowCreateSource] = useState(false);
  const [newSourceTitle, setNewSourceTitle] = useState('');
  const [newSourceAuthor, setNewSourceAuthor] = useState('');
  const [newSourceCategory, setNewSourceCategory] = useState('');
  const [newSourceUrl, setNewSourceUrl] = useState('');
  const [creating, setCreating] = useState(false);

  // Add highlight to custom source
  const [showAddHighlight, setShowAddHighlight] = useState(false);
  const [newHighlightText, setNewHighlightText] = useState('');
  const [newHighlightNote, setNewHighlightNote] = useState('');
  const [addingHighlight, setAddingHighlight] = useState(false);

  // User-sichtbares Fehler-Feedback
  const [formError, setFormError] = useState('');

  // Lazy Loading state (moved up to respect Hook ordering rules)
  const [sourceLoading, setSourceLoading] = useState(false);

  // Sort state
  const [sortBy, setSortBy] = useState('last_synced_desc');

  // Keyboard Navigation state
  const [kbSourceIdx, setKbSourceIdx] = useState(-1);   // Index in filtered (Quellenliste)
  const [kbHlIdx, setKbHlIdx] = useState(-1);           // Index in highlights (Detail-Ansicht)
  const [kbAction, setKbAction] = useState(null);        // 'expand' | 'edit' | 'link' | null

  useEffect(() => {
    // Bootstrap-Daten wurden bereits via Props gesetzt — nur nachladen wenn nicht vorhanden
    if (!initialHighlightZettelMap) fetchHighlightZettelMap().then(setHighlightZettelMap).catch(() => {});
    if (!initialHighlightStatus) fetchHighlightStatus().then(setHighlightStatus).catch(() => {});
    if (initialSyncing) { startPolling(); } else if (initialSyncing === undefined) {
      fetchSyncStatus().then(s => { if (s.syncing) startPolling(); }).catch(() => {});
    }
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []); // eslint-disable-line

  useEffect(() => {
    if (refreshKey > 0) fetchHighlightZettelMap().then(setHighlightZettelMap).catch(() => {});
  }, [refreshKey]);

  // Echtzeit-Unlink: Wenn ein Highlight im Editor entfernt wird, sofort den Link-Chip
  // aus der lokalen Map entfernen — ohne Warten auf Server-Antwort oder Reload.
  useEffect(() => {
    if (!highlightUnlinkEvent) return;
    const { zettelId, highlightRef } = highlightUnlinkEvent;
    setHighlightZettelMap(prev => {
      if (!prev[highlightRef]) return prev;
      const filtered = prev[highlightRef].filter(id => String(id) !== String(zettelId));
      const updated = { ...prev };
      if (filtered.length === 0) {
        delete updated[highlightRef];
      } else {
        updated[highlightRef] = filtered;
      }
      return updated;
    });
  }, [highlightUnlinkEvent]);

  // Echtzeit-Link: Wenn ein Highlight via ⌘V in eine Notiz eingefügt wird, sofort den Link-Chip
  // in der lokalen Map ergänzen — ohne Warten auf Server-Refetch.
  useEffect(() => {
    if (!highlightLinkEvent) return;
    const { zettelId, highlightRef } = highlightLinkEvent;
    setHighlightZettelMap(prev => {
      const existing = prev[highlightRef] || [];
      if (existing.some(id => String(id) === String(zettelId))) return prev;
      return { ...prev, [highlightRef]: [...existing, zettelId] };
    });
  }, [highlightLinkEvent]);

  useEffect(() => {
    if (resetKey > 0) {
      if (onRefreshSources) onRefreshSources();
      setSelectedSource(null);
      fetchHighlightZettelMap().then(setHighlightZettelMap).catch(() => {});
      fetchHighlightStatus().then(setHighlightStatus).catch(() => {});
    }
  }, [resetKey]); // eslint-disable-line

  useEffect(() => {
    if (!initialHighlightRef || sources.length === 0) return;
    const isCu = initialHighlightRef.startsWith('cu-');
    const match = initialHighlightRef.replace(/^(rw|cu)-/, '').match(/^(.+)-(\d+)$/);
    if (!match) return;
    const source = isCu
      ? sources.find(s => String(s.custom_id) === String(match[1]))
      : sources.find(s => String(s.readwise_id) === String(match[1]));
    if (source) {
      handleSelectSource(source).then(() => {
        setTargetHighlightRef(initialHighlightRef);
        if (onHighlightRefUsed) onHighlightRefUsed();
      });
    }
  }, [initialHighlightRef, sources]); // eslint-disable-line

  // Navigate to a specific source when selected from the command palette
  useEffect(() => {
    if (!initialSourceId || sources.length === 0) return;
    const source = sources.find(s => String(s.readwise_id) === String(initialSourceId) || String(s.custom_id) === String(initialSourceId));
    if (source) {
      handleSelectSource(source).then(() => {
        if (onSourceSelected) onSourceSelected();
      });
    }
  }, [initialSourceId, sources]); // eslint-disable-line

  const refreshSources = useCallback(() => {
    if (onRefreshSources) onRefreshSources();
  }, [onRefreshSources]);

  // Lazy Loading: Quelle anklicken → Details nachladen (wenn slim)
  // Client-Cache: einmal geladene Quellen bleiben im Speicher
  const sourceDetailCache = useRef(new Map());
  const handleSelectSource = useCallback(async (source) => {
    // Wenn bereits volle Highlights da sind → direkt anzeigen
    if (source.highlights && source.highlights.length > 0) {
      setSelectedSource(source);
      return;
    }
    const key = source.is_custom ? source.custom_id : source.readwise_id;
    if (!key) { setSelectedSource(source); return; }
    // Client-Cache prüfen
    const cacheKey = String(key);
    if (sourceDetailCache.current.has(cacheKey)) {
      setSelectedSource(sourceDetailCache.current.get(cacheKey));
      return;
    }
    // Slim-Modus: Details vom Server nachladen
    setSourceLoading(true);
    try {
      const full = await fetchReadwiseSource(cacheKey);
      sourceDetailCache.current.set(cacheKey, full);
      setSelectedSource(full);
    } catch (e) {
      setSelectedSource(source);
    } finally {
      setSourceLoading(false);
    }
  }, []);

  // Cache-Eintrag invalidieren wenn Highlights geändert werden
  const invalidateSourceCache = useCallback((source) => {
    if (!source) return;
    const key = source.is_custom ? source.custom_id : source.readwise_id;
    if (key) sourceDetailCache.current.delete(String(key));
  }, []);

  const startPolling = () => {
    setSyncing(true); setSyncMessage('Synchronisiere...');
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearInterval(pollRef.current);
        return;
      }
      try {
        const status = await fetchSyncStatus();
        if (!isMountedRef.current) { clearInterval(pollRef.current); return; }
        if (!status.syncing) {
          clearInterval(pollRef.current); pollRef.current = null;
          setSyncing(false); setSyncMessage('Sync abgeschlossen!');
          sourceDetailCache.current.clear(); // Cache nach Sync komplett leeren
          refreshSources();
          // Zweiter Refresh nach 3s — stellt sicher, dass alle Daten (inkl. Cover-Images) geladen sind
          setTimeout(() => { if (isMountedRef.current) refreshSources(); }, 3000);
          setTimeout(() => setSyncMessage(''), 4000);
        }
      } catch (e) { clearInterval(pollRef.current); if (isMountedRef.current) setSyncing(false); }
    }, 2000);
  };

  const handleSync = async (full = false) => {
    if (syncing || readOnly) return;
    try {
      await triggerReadwiseSync(full);
      startPolling();
      // Schicht 2: Beim Sync parallel den Trial-Server anpingen (non-blocking)
      if (window.electronAPI?.license?.serverCheck) {
        window.electronAPI.license.serverCheck().catch(() => { /* intentional: Server nicht erreichbar ist OK */ });
      }
    }
    catch (e) { setSyncing(false); setSyncMessage('Fehler!'); }
  };

  // handleDeleteHighlight: nach handleStatusChange deklariert (TDZ-Schutz)

  const handleUnlinkZettel = async (zettelId, highlightRef) => {
    if (readOnly) return;
    // Optimistic update: immediately remove from local map so UI reflects change
    setHighlightZettelMap(prev => {
      const updated = { ...prev };
      if (highlightRef && updated[highlightRef]) {
        updated[highlightRef] = updated[highlightRef].filter(id => id !== zettelId);
        if (updated[highlightRef].length === 0) delete updated[highlightRef];
      } else {
        for (const ref of Object.keys(updated)) {
          updated[ref] = updated[ref].filter(id => id !== zettelId);
          if (updated[ref].length === 0) delete updated[ref];
        }
      }
      return updated;
    });

    try {
      // Auch den <details>-Block aus dem Zettel-Content entfernen
      if (onUnlinkFromNote) {
        await onUnlinkFromNote(zettelId, highlightRef);
      } else {
        const result = await updateZettel(zettelId, { remove_highlight_ref: highlightRef });
        if (result.highlight_ref && result.highlight_ref !== '') {
          // Kann korrekt sein wenn der Zettel noch andere Highlights hat
          console.log('Zettel hat noch weitere highlight_refs:', result.highlight_ref);
        }
      }

      // Short delay so chokidar/cache can settle, then confirm with fresh map
      await new Promise(r => setTimeout(r, 300));
      const newMap = await fetchHighlightZettelMap();
      setHighlightZettelMap(newMap);
    } catch (e) {
      console.error('Fehler beim Aufheben der Verknüpfung:', e);
      fetchHighlightZettelMap().then(setHighlightZettelMap).catch(() => {});
    }
  };

  // Highlight-Status setzen (optimistisches Update)
  const handleStatusChange = useCallback(async (ref, newStatus) => {
    if (readOnly) return;
    setHighlightStatus(prev => {
      if (newStatus === 'offen') {
        const next = { ...prev };
        delete next[ref];
        return next;
      }
      return { ...prev, [ref]: newStatus };
    });
    try {
      await apiSetHighlightStatus(ref, newStatus);
    } catch (e) {
      console.error('Fehler beim Setzen des Status:', e);
      // Rollback auf Server-Stand
      fetchHighlightStatus().then(setHighlightStatus).catch(() => {});
    }
  }, []);

  // Soft-Delete: Status auf 'gelöscht' setzen (kein Hard-Delete, kein File-I/O)
  const handleDeleteHighlight = useCallback((highlight) => {
    handleStatusChange(highlight.ref, 'gelöscht');
  }, [handleStatusChange]);

  // --- Custom Source Handlers ---
  // Alle mit useCallback gewrappt, um unnötige HighlightCard-Rerenders zu vermeiden.
  // Kein doppeltes fetchReadwise() — sources kommen als Prop von App.js.

  const handleCreateSource = useCallback(async () => {
    if (readOnly) return;
    if (!newSourceTitle.trim()) return;
    setFormError('');
    setCreating(true);
    try {
      await createCustomSource({
        title: newSourceTitle.trim(),
        author: newSourceAuthor.trim(),
        category: newSourceCategory.trim().toLowerCase() || 'custom',
        source_url: newSourceUrl.trim(),
      });
      refreshSources();
      setNewSourceTitle(''); setNewSourceAuthor(''); setNewSourceCategory(''); setNewSourceUrl('');
      setShowCreateSource(false);
    } catch (e) {
      console.error('Fehler beim Erstellen:', e);
      setFormError('Quelle konnte nicht erstellt werden.');
    } finally {
      setCreating(false);
    }
  }, [newSourceTitle, newSourceAuthor, newSourceCategory, newSourceUrl]); // eslint-disable-line

  const handleAddHighlight = useCallback(async () => {
    if (readOnly) return;
    if (!newHighlightText.trim() || !selectedSource?.is_custom) return;
    invalidateSourceCache(selectedSource);
    setFormError('');
    setAddingHighlight(true);
    try {
      const result = await addCustomHighlight(selectedSource.custom_id, {
        text: newHighlightText.trim(),
        note: newHighlightNote.trim(),
      });
      // Optimistic: add to local selectedSource
      if (result?.highlight) {
        setSelectedSource(prev => prev ? { ...prev, highlights: [...(prev.highlights || []), result.highlight] } : prev);
      }
      setNewHighlightText(''); setNewHighlightNote(''); setShowAddHighlight(false);
      // Deferred refresh for consistency
      refreshSources();
    } catch (e) {
      console.error('Fehler beim Hinzufügen:', e);
      setFormError('Highlight konnte nicht gespeichert werden.');
    } finally {
      setAddingHighlight(false);
    }
  }, [newHighlightText, newHighlightNote, selectedSource, refreshSources]); // eslint-disable-line

  const handleDeleteCustomSource = useCallback(async (source) => {
    if (readOnly) return;
    if (!source?.is_custom || !window.confirm(`Quelle "${source.title}" wirklich löschen?`)) return;
    invalidateSourceCache(source);
    try {
      await deleteCustomSource(source.custom_id);
      refreshSources();
      fetchHighlightStatus().then(setHighlightStatus).catch(() => {});
      setSelectedSource(prev => prev?.custom_id === source.custom_id ? null : prev);
    } catch (e) {
      console.error('Fehler beim Löschen:', e);
      setFormError('Quelle konnte nicht gelöscht werden.');
    }
  }, []); // eslint-disable-line

  const handleDeleteCustomHighlight = useCallback(async (highlight) => {
    if (readOnly) return;
    if (!selectedSource?.is_custom) return;
    if (!window.confirm('Highlight wirklich löschen?')) return;
    invalidateSourceCache(selectedSource);
    try {
      await deleteCustomHighlight(selectedSource.custom_id, highlight.index);
      // Optimistic: remove highlight locally
      setSelectedSource(prev => {
        if (!prev) return prev;
        return { ...prev, highlights: (prev.highlights || []).filter(h => h.ref !== highlight.ref) };
      });
      fetchHighlightZettelMap().then(setHighlightZettelMap).catch(() => {});
      refreshSources();
    } catch (e) {
      console.error('Fehler beim Löschen:', e);
      setFormError('Highlight konnte nicht gelöscht werden.');
    }
  }, [selectedSource, refreshSources]); // eslint-disable-line

  const handleEditHighlight = useCallback(async (highlight, newText, newNote) => {
    if (readOnly) return;
    if (!selectedSource) return;
    invalidateSourceCache(selectedSource);

    // 1. Optimistic Update — sofort im UI sichtbar
    const oldHighlights = selectedSource.highlights;
    setSelectedSource(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        highlights: (prev.highlights || []).map(h =>
          h.ref === highlight.ref ? { ...h, text: newText, note: newNote } : h
        )
      };
    });

    // 2. Sofort Zettel-Editor benachrichtigen (0ms Latenz)
    if (onHighlightEdited) onHighlightEdited(highlight.ref, newText);

    // 3. Server-Update im Hintergrund — KEIN full re-fetch!
    try {
      if (selectedSource.is_custom) {
        await updateCustomHighlight(selectedSource.custom_id, highlight.index, { text: newText, note: newNote });
      } else {
        await updateReadwiseHighlight(String(selectedSource.readwise_id), highlight.index, { text: newText, note: newNote });
      }
      // No full re-fetch needed — optimistic update is sufficient for edits
    } catch (e) {
      // Rollback bei Fehler
      setSelectedSource(prev => {
        if (!prev) return prev;
        return { ...prev, highlights: oldHighlights };
      });
      console.error('Fehler beim Bearbeiten:', e);
      setFormError('Highlight konnte nicht gespeichert werden.');
      throw e;
    }
  }, [selectedSource, onHighlightEdited]); // eslint-disable-line

  // ─── Keyboard Navigation ───
  // panelRef entfernt — querySelector läuft direkt auf document (panelRef war nie im JSX attached)

  // KB-Index zurücksetzen wenn Ansicht wechselt — bei targetHighlightRef auf das Ziel-Highlight setzen
  useEffect(() => {
    if (!selectedSource) { setKbHlIdx(-1); return; }
    if (targetHighlightRef && highlights.length > 0) {
      const targetIdx = highlights.findIndex(h => h.ref === targetHighlightRef);
      setKbHlIdx(targetIdx >= 0 ? targetIdx : 0);
    } else {
      setKbHlIdx(0);
    }
  }, [selectedSource]); // eslint-disable-line
  useEffect(() => { setKbSourceIdx(-1); }, [filter, triageFilter, searchQuery]);

  // Report highlight focus state to parent for context-dependent hints
  useEffect(() => {
    if (onHighlightFocusChange) onHighlightFocusChange(kbHlIdx >= 0);
  }, [kbHlIdx]); // eslint-disable-line

  // Focus-Handler: Beim Fokussieren der Sidebar Keyboard-Navigation initialisieren
  useEffect(() => {
    const sidebar = document.querySelector('.panel-sidebar');
    if (!sidebar) return;
    const handleFocus = () => {
      if (selectedSource) {
        // Detail-Ansicht: Ersten Highlight fokussieren wenn keiner ausgewählt
        setKbHlIdx(prev => prev >= 0 ? prev : 0);
      } else {
        // Quellenliste: Erste Quelle fokussieren wenn keine ausgewählt
        setKbSourceIdx(prev => prev >= 0 ? prev : 0);
      }
    };
    sidebar.addEventListener('focus', handleFocus);
    return () => sidebar.removeEventListener('focus', handleFocus);
  }, [selectedSource]);

  // ANDREAS-2: Feste Reihenfolge statt Set-Insertion-Order
  const CATEGORY_ORDER = ['books', 'articles', 'podcasts', 'videos', 'tweets', 'kurse', 'vorträge', 'gespräche', 'supplementals'];
  const categoriesPresent = new Set(sources.map(s => s.category));
  const categories = CATEGORY_ORDER.filter(c => categoriesPresent.has(c))
    .concat([...categoriesPresent].filter(c => !CATEGORY_ORDER.includes(c)));

  // Helper: Triage-Status eines Highlights ermitteln (default: 'offen')
  const getHlStatus = (ref) => highlightStatus[ref] || 'offen';

  // Helper: Refs einer Quelle ermitteln (funktioniert mit slim + full mode)
  const getSourceRefs = useCallback((src) => {
    if (src.highlight_refs) return src.highlight_refs;
    return (src.highlights || []).map(h => h.ref);
  }, []);

  // OPT: useMemo statt IIFE — nur neu berechnen wenn sources oder highlightStatus sich ändern
  const triageStats = useMemo(() => {
    let total = 0, archiviert = 0, gemerkt = 0;
    for (const src of sources) {
      for (const ref of getSourceRefs(src)) {
        total++;
        const s = highlightStatus[ref] || 'offen';
        if (s === 'archiviert') archiviert++;
        else if (s === 'gemerkt') gemerkt++;
      }
    }
    return { total, archiviert, gemerkt, offen: total - archiviert - gemerkt };
  }, [sources, highlightStatus, getSourceRefs]);

  // OPT: useMemo — nur neu filtern/sortieren wenn sich Eingaben tatsächlich ändern
  const filtered = useMemo(() => {
    let list = filter === 'all' ? [...sources] : sources.filter(s => s.category === filter);

    // Triage-Filter: Quellen ohne passende Highlights ausblenden
    // Sonderfall: Quellen mit 0 Highlights gelten als "offen" (noch nicht bearbeitet)
    if (triageFilter !== 'alle') {
      list = list.filter(src => {
        const refs = getSourceRefs(src);
        if (refs.length === 0) return triageFilter === 'offen'; // leere Quellen = offen
        if (triageFilter === 'offen') {
          return refs.some(ref => (highlightStatus[ref] || 'offen') !== 'archiviert');
        }
        if (triageFilter === 'gemerkt') {
          return refs.some(ref => (highlightStatus[ref] || 'offen') === 'gemerkt');
        }
        return true;
      });
    }

    // Search filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        (s.title || '').toLowerCase().includes(q) ||
        (s.author || '').toLowerCase().includes(q)
      );
    }

    const getDate = (s) => s.last_highlight_at || s.first_highlight_at || s.created_at || '';
    if (sortBy === 'az') list.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
    else if (sortBy === 'za') list.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
    else if (sortBy === 'last_synced_desc') list.sort((a, b) => {
      const da = getDate(a), db = getDate(b);
      if (!da && !db) return (a.title || '').localeCompare(b.title || '');
      if (!da) return 1;
      if (!db) return -1;
      return db.localeCompare(da) || (a.title || '').localeCompare(b.title || '');
    });
    else if (sortBy === 'last_synced_asc') list.sort((a, b) => {
      const da = getDate(a), db = getDate(b);
      if (!da && !db) return (a.title || '').localeCompare(b.title || '');
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db) || (a.title || '').localeCompare(b.title || '');
    });
    return list;
  }, [sources, filter, triageFilter, highlightStatus, searchQuery, sortBy]);

  // OPT: useMemo — Highlights der aktuellen Quelle filtern (Triage)
  // Ausnahme: targetHighlightRef wird immer angezeigt (Direktlink-Bypass)
  const allHighlights = selectedSource?.highlights || [];
  const [showDeleted, setShowDeleted] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const archivedCount = allHighlights.filter(h => getHlStatus(h.ref) === 'archiviert').length;
  const deletedCount = allHighlights.filter(h => getHlStatus(h.ref) === 'gelöscht').length;
  const highlights = useMemo(() => {
    let list;
    if (triageFilter === 'alle') {
      list = allHighlights;
    } else {
      list = allHighlights.filter(h => {
        const s = getHlStatus(h.ref);
        if (s === 'gelöscht' || s === 'archiviert') return true; // Erledigt/Ausgeblendet-Filter passiert separat unten
        return h.ref === targetHighlightRef ||
          (triageFilter === 'offen') ||
          (triageFilter === 'gemerkt' && s === 'gemerkt');
      });
    }
    // Erledigte ausblenden (außer Toggle aktiv ODER targetHighlightRef)
    if (!showArchived) {
      list = list.filter(h => h.ref === targetHighlightRef || getHlStatus(h.ref) !== 'archiviert');
    }
    // Ausgeblendete ausblenden (außer Toggle aktiv ODER targetHighlightRef)
    if (!showDeleted) {
      list = list.filter(h => h.ref === targetHighlightRef || getHlStatus(h.ref) !== 'gelöscht');
    }
    return list;
  }, [allHighlights, triageFilter, targetHighlightRef, highlightStatus, showDeleted, showArchived]); // eslint-disable-line
  const hiddenCount = allHighlights.length - highlights.length;

  // ─── Keyboard Navigation Handler (NACH filtered + highlights, um TDZ zu vermeiden) ───
  const handlePanelKeyDown = useCallback((e) => {
    // Keine Navigation wenn Eingabefeld oder Editor fokussiert
    const active = document.activeElement;
    const tag = active?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    if (active?.closest('.cm-editor')) return;

    if (!selectedSource) {
      // ─── Quellenliste-Navigation ───
      const len = filtered.length;
      if (!len) return;
      // Auto-Init: Beim ersten Tastendruck auf erste Quelle setzen
      if (kbSourceIdx < 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        setKbSourceIdx(0);
        document.querySelector('[data-source-idx="0"]')?.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setKbSourceIdx(i => {
          const next = Math.min(i + 1, len - 1);
          document.querySelector(`[data-source-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setKbSourceIdx(i => {
          const next = Math.max(i - 1, 0);
          document.querySelector(`[data-source-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'Enter' && kbSourceIdx >= 0) {
        e.preventDefault();
        handleSelectSource(filtered[kbSourceIdx]);
      }
    } else {
      // ─── Highlight-Detail-Navigation ───
      const len = highlights.length;
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedSource(null);
        setTargetHighlightRef(null);
        return;
      }
      if (!len) return;
      // Auto-Init: Beim ersten Tastendruck auf erstes Highlight setzen
      if (kbHlIdx < 0 && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
        e.preventDefault();
        setKbHlIdx(0);
        document.querySelector('[data-hl-idx="0"]')?.scrollIntoView({ block: 'nearest' });
        return;
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setKbHlIdx(i => {
          const next = Math.min(i + 1, len - 1);
          document.querySelector(`[data-hl-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setKbHlIdx(i => {
          const next = Math.max(i - 1, 0);
          document.querySelector(`[data-hl-idx="${next}"]`)?.scrollIntoView({ block: 'nearest' });
          return next;
        });
      } else if (e.key === 'ArrowRight' && kbHlIdx >= 0) {
        e.preventDefault();
        setKbAction('open');
      } else if (e.key === 'ArrowLeft' && kbHlIdx >= 0) {
        e.preventDefault();
        setKbAction('close');
      } else if (e.key === 'Enter' && kbHlIdx >= 0) {
        e.preventDefault();
        setKbAction('edit');
      } else if ((e.key === 'e' || e.key === 'E') && kbHlIdx >= 0) {
        e.preventDefault();
        setKbAction('edit');
      } else if ((e.key === 'l' || e.key === 'L') && kbHlIdx >= 0 && !e.metaKey && !e.ctrlKey) {
        // L = Verlinken: Einfüge-Modus im Baum aktivieren
        const targetIdx = kbHlIdx >= 0 ? kbHlIdx : 0;
        if (highlights[targetIdx] && onLinkHighlight) {
          e.preventDefault();
          onLinkHighlight(highlights[targetIdx], 'insert');
          // Fokus auf die Baumstruktur setzen, damit Pfeiltasten dort navigieren
          setTimeout(() => {
            const tree = document.querySelector('.zettel-tree');
            if (tree) tree.focus();
          }, 50);
        }
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'c' || e.key === 'C') && kbHlIdx >= 0) {
        // ⌘C = Highlight-Marker in Zwischenablage kopieren — nur in Preview, nicht im Edit-Modus
        const isInEditor = document.activeElement?.closest('.cm-editor') || document.activeElement?.closest('.hl-edit-area');
        if (isInEditor) return; // Im Edit-Modus: native ⌘C (Text kopieren) nicht überschreiben
        const hl = highlights[kbHlIdx];
        if (hl) {
          e.preventDefault();
          const marker = `{{highlight:${hl.ref || hl.id}}}`;
          navigator.clipboard.writeText(marker).catch(() => {});
          setKbAction('copy');
          if (onHighlightCopied) onHighlightCopied();
          if (onShowShortcutTip) onShowShortcutTip('highlight-paste', 'Highlight mit ⌘V in Notiz einfügen');
        }
      }
    }
  }, [selectedSource, filtered, highlights, kbSourceIdx, kbHlIdx, handleSelectSource]); // eslint-disable-line

  // Keyboard-Navigation: document-level listener, aktiv wenn Panel-Sidebar fokussiert ist
  useEffect(() => {
    const onKeyDown = (e) => {
      const sidebar = document.querySelector('.panel-sidebar');
      if (!sidebar) return;
      // Nur aktiv wenn Fokus im Sidebar-Panel liegt
      if (!sidebar.contains(document.activeElement) && document.activeElement !== sidebar) return;
      handlePanelKeyDown(e);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [handlePanelKeyDown]);

  return (<>

    {/* Loading overlay when fetching source details */}
    {sourceLoading && !selectedSource && (
      <div className="readwise-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.9rem' }}>
          <div style={{ marginBottom: '8px', fontSize: '1.2rem', opacity: 0.5 }}>⏳</div>
          Lade Highlights…
        </div>
      </div>
    )}
    {/* Detail view — only mounted when a source is selected */}
    {selectedSource && (
      <div className="readwise-panel readwise-panel--detail">
        <div className="readwise-detail-content">
          <div className="hl-detail-sticky-header">
            <button onClick={() => {
                if (onShowShortcutTip) onShowShortcutTip('back-to-library', <>Tipp: Drücke <kbd>Esc</kbd> um schneller zurückzukehren</>);
                setSelectedSource(null); setTargetHighlightRef(null);
              }}
              className="hl-back-btn">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
              Zurück zur Bibliothek
              <span className="shortcut-inline-hint">Esc</span>
            </button>
            <div className="hl-detail-header">
              <h2 className="hl-detail-title">
                {selectedSource.is_custom && <span className="custom-source-badge" title="Eigene Quelle" style={{ fontSize: '0.75rem' }}>✏️</span>}
                {selectedSource.title || '(Ohne Titel)'}
              </h2>
              <span className="hl-meta-text">
                {selectedSource.author} · {highlights.length}{hiddenCount > 0 ? ` von ${allHighlights.length}` : ''} Highlights
                {hiddenCount > 0 && <span style={{ marginLeft: 6, fontStyle: 'italic' }}>({hiddenCount} archiviert)</span>}
              </span>
            </div>
            {selectedSource.is_custom && (
              <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                <button className="custom-add-hl-btn" onClick={() => setShowAddHighlight(s => !s)}>
                  <InsertIcon /> Highlight hinzufügen
                </button>
                <button className="custom-delete-src-btn" onClick={() => handleDeleteCustomSource(selectedSource)}>
                  Quelle löschen
                </button>
              </div>
            )}
          </div>

          {/* Inline form: Highlight hinzufügen (nur bei eigenen Quellen) */}
          {showAddHighlight && selectedSource.is_custom && (
            <div className="custom-form" onKeyDown={e => {
              if (e.key === 'Escape') { setShowAddHighlight(false); setNewHighlightText(''); setNewHighlightNote(''); setFormError(''); }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && newHighlightText.trim()) handleAddHighlight();
            }}>
              <textarea
                className="custom-form-textarea"
                placeholder="Highlight-Text eingeben..."
                value={newHighlightText}
                onChange={e => setNewHighlightText(e.target.value)}
                rows={3}
                autoFocus
              />
              <input
                className="custom-form-input"
                type="text"
                placeholder="Notiz (optional)"
                value={newHighlightNote}
                onChange={e => setNewHighlightNote(e.target.value)}
              />
              {formError && <div className="custom-form-error">{formError}</div>}
              <div className="custom-form-actions">
                <span className="custom-form-hint">⌘↵ Speichern · Esc Abbrechen</span>
                <button className="custom-form-save" onClick={handleAddHighlight} disabled={addingHighlight || !newHighlightText.trim()}>
                  {addingHighlight ? 'Speichern...' : 'Speichern'}
                </button>
                <button className="custom-form-cancel" onClick={() => { setShowAddHighlight(false); setNewHighlightText(''); setNewHighlightNote(''); setFormError(''); }}>
                  Abbrechen
                </button>
              </div>
            </div>
          )}

          {highlights.length === 0 && !showAddHighlight && <p style={{ color: 'var(--color-text-tertiary)', fontStyle: 'italic', fontSize: 'var(--text-sm)' }}>Keine Highlights.</p>}

          {highlights.map((h, hlIdx) => (
            <div key={h.ref} data-hl-idx={hlIdx} onClick={() => setKbHlIdx(hlIdx)}>
              <HighlightCard
                highlight={h}
                source={selectedSource}
                zettelIds={highlightZettelMap[h.ref] || []}
                onOpenZettel={(id) => onOpenZettel && onOpenZettel(id)}
                isTarget={h.ref === targetHighlightRef}
                onLinkHighlight={onLinkHighlight}
                onDelete={selectedSource.is_custom ? handleDeleteCustomHighlight : handleDeleteHighlight}
                onEdit={handleEditHighlight}
                onUnlink={handleUnlinkZettel}
                status={getHlStatus(h.ref)}
                onStatusChange={handleStatusChange}
                sourceKey={selectedSource.is_custom
                  ? `custom/${selectedSource.custom_id}`
                  : `${selectedSource.category}/${selectedSource.filename}`}
                isKbFocused={kbHlIdx === hlIdx}
                kbAction={kbHlIdx === hlIdx ? kbAction : null}
                onKbActionHandled={() => setKbAction(null)}
                onEditingChange={onHighlightEditingChange}
                onHighlightPreview={onHighlightEdited ? (ref, text) => onHighlightEdited(ref, text) : undefined}
              />
            </div>
          ))}
          {(archivedCount > 0 || deletedCount > 0) && (
            <div className="hl-toggles-footer">
              {archivedCount > 0 && (
                <button onClick={() => setShowArchived(prev => !prev)} className="hl-footer-toggle">
                  {showArchived
                    ? `✓ ${archivedCount} erledigte${archivedCount !== 1 ? '' : 's'} Highlight${archivedCount !== 1 ? 's' : ''} verbergen`
                    : `${archivedCount} erledigte${archivedCount !== 1 ? '' : 's'} Highlight${archivedCount !== 1 ? 's' : ''} einblenden`
                  }
                </button>
              )}
              {deletedCount > 0 && (
                <button onClick={() => setShowDeleted(prev => !prev)} className="hl-footer-toggle">
                  {showDeleted
                    ? `✓ ${deletedCount} ausgeblendete${deletedCount !== 1 ? '' : 's'} Highlight${deletedCount !== 1 ? 's' : ''} verbergen`
                    : `${deletedCount} ausgeblendete${deletedCount !== 1 ? '' : 's'} Highlight${deletedCount !== 1 ? 's' : ''} einblenden`
                  }
                </button>
              )}
              {(archivedCount > 0 || deletedCount > 0) && (!showArchived || !showDeleted) && (archivedCount + deletedCount > 1) && (
                <button onClick={() => { setShowArchived(true); setShowDeleted(true); }} className="hl-footer-toggle hl-footer-toggle-all">
                  Alle anzeigen
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Overview — always in DOM so scrollTop is preserved naturally */}
    <div className="readwise-panel" style={{ display: selectedSource ? 'none' : '' }}>
      {/* Search */}
      <div className="sidebar-search">
        <div className="search-wrapper">
          <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className="search-input"
            placeholder="Suchen..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            onKeyDown={e => {
              if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
                e.preventDefault();
                if (onOpenCmd) onOpenCmd();
              }
            }}
          />
          <kbd className="search-kbd" onClick={() => onOpenCmd && onOpenCmd()} style={{ cursor: 'pointer' }}>⌘K</kbd>
        </div>
      </div>

      {/* Triage Filter + Fortschritt */}
      {triageStats.total > 0 && (
        <div className="triage-section">
          <div className="triage-row">
            <div className="triage-tabs">
              {[
                { key: 'offen', label: 'Offen', count: triageStats.offen },
                { key: 'gemerkt', label: 'Gemerkt', count: triageStats.gemerkt },
                { key: 'alle', label: 'Alle', count: triageStats.total },
              ].map(({ key, label, count }) => (
                <button
                  key={key}
                  className={`triage-tab${triageFilter === key ? ' active' : ''}`}
                  onClick={() => setTriageFilter(key)}
                  aria-label={`Filtern nach: ${label} (${count})`}
                >
                  {label}
                  <span className="triage-tab-count">{count}</span>
                </button>
              ))}
            </div>
            {triageStats.archiviert > 0 && (
              <span className="triage-archiviert-label">
                {triageStats.archiviert} archiviert
              </span>
            )}
          </div>
          {triageStats.archiviert > 0 && (
            <div className="triage-progress-bar">
              <div
                className="triage-progress-fill"
                style={{ width: `${Math.round((triageStats.archiviert / triageStats.total) * 100)}%` }}
              />
            </div>
          )}
        </div>
      )}

      {/* Filter Tabs (Kategorie) */}
      <div className={`filter-tabs${filter !== 'all' ? ' has-subfilter' : ''}`}>
        {['all', ...categories].map(cat => {
          const isActive = filter === cat;
          const isAlle = cat === 'all';
          let cls = 'filter-tab';
          if (isAlle) cls += ' filter-tab-alle';
          if (isActive && isAlle) cls += ' active-default';
          if (isActive && !isAlle) cls += ' active-sub';
          return (
            <button
              key={cat}
              className={cls}
              onClick={() => setFilter(cat)}
              aria-label={`Filtern nach: ${isAlle ? 'Alle' : getCategoryLabel(cat)}`}
            >
              {isAlle ? 'Alle' : (getCategoryLabel(cat))}
            </button>
          );
        })}
      </div>

      {/* Sort & Sync */}
      <div className="sort-sync">
        <button className="sort-btn" onClick={() => {
          const order = ['last_synced_desc', 'last_synced_asc', 'az', 'za'];
          setSortBy(order[(order.indexOf(sortBy) + 1) % order.length]);
        }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 15l5 5 5-5"/><path d="M7 9l5-5 5 5"/>
          </svg>
          <span>{{ last_synced_desc: 'Neueste zuerst', last_synced_asc: 'Älteste zuerst', az: 'A → Z', za: 'Z → A' }[sortBy]}</span>
        </button>
        {!readOnly && (
          <button className="sort-btn" onClick={() => setShowCreateSource(s => !s)} title="Eigene Quelle erstellen" style={{ color: showCreateSource ? 'var(--color-primary)' : undefined }}>
            <InsertIcon />
            <span>Quelle</span>
          </button>
        )}
        {!readOnly && (
          <button className={`sync-btn ${syncing ? 'syncing' : ''}`} onClick={() => !syncing && handleSync(false)} disabled={syncing} aria-label="Readwise synchronisieren">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
            </svg>
            <span>{syncing ? 'Läuft...' : 'Sync'}</span>
          </button>
        )}
      </div>

      {syncMessage && (
        <div style={{ padding: '0 var(--space-4) var(--space-2)', fontSize: 'var(--text-xs)', color: syncing ? 'var(--color-accent-warm)' : 'var(--color-primary)' }}>
          {syncMessage}
        </div>
      )}

      {/* Source List */}
      <div className="source-list">
        {/* Inline-Formular: Eigene Quelle erstellen (oben in der Liste) */}
        {showCreateSource && (
          <div className="custom-form" style={{ margin: '0 var(--space-3) var(--space-2)' }} onKeyDown={e => {
            if (e.key === 'Escape') { setShowCreateSource(false); setNewSourceTitle(''); setNewSourceAuthor(''); setNewSourceCategory(''); setNewSourceUrl(''); setFormError(''); }
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && newSourceTitle.trim()) handleCreateSource();
          }}>
            <input className="custom-form-input" type="text" placeholder="Titel *" value={newSourceTitle} onChange={e => setNewSourceTitle(e.target.value)} autoFocus />
            <input className="custom-form-input" type="text" placeholder="Autor (optional)" value={newSourceAuthor} onChange={e => setNewSourceAuthor(e.target.value)} />
            <div className="custom-category-row">
              <input className="custom-form-input" type="text" placeholder="Kategorie (z.B. Kurs, Vortrag)" value={newSourceCategory} onChange={e => setNewSourceCategory(e.target.value)} style={{ flex: 1 }} />
            </div>
            {newSourceCategory === '' && (
              <div className="custom-category-suggestions">
                {CUSTOM_CATEGORY_SUGGESTIONS.map(cat => (
                  <button key={cat} className="custom-category-chip" onClick={() => setNewSourceCategory(cat)}>{getCategoryLabel(cat)}</button>
                ))}
              </div>
            )}
            <input className="custom-form-input" type="text" placeholder="URL (optional)" value={newSourceUrl} onChange={e => setNewSourceUrl(e.target.value)} />
            {formError && <div className="custom-form-error">{formError}</div>}
            <div className="custom-form-actions">
              <span className="custom-form-hint">⌘↵ Erstellen · Esc Abbrechen</span>
              <button className="custom-form-save" onClick={handleCreateSource} disabled={creating || !newSourceTitle.trim()}>{creating ? 'Erstellen...' : 'Erstellen'}</button>
              <button className="custom-form-cancel" onClick={() => { setShowCreateSource(false); setNewSourceTitle(''); setNewSourceAuthor(''); setNewSourceCategory(''); setNewSourceUrl(''); setFormError(''); }}>Abbrechen</button>
            </div>
          </div>
        )}

        {filtered.map((source, idx) => {
          const refs = getSourceRefs(source);
          const linkedCount = refs.filter(ref => (highlightZettelMap[ref] || []).length > 0).length;
          const srcArchiviert = refs.filter(ref => getHlStatus(ref) === 'archiviert').length;
          const srcTotal = refs.length;
          const srcOffen = srcTotal - srcArchiviert;
          const srcDone = srcTotal > 0 && srcOffen === 0;
          // Preview: aus slim-Daten oder aus Highlights
          const previewText = source.preview
            ? source.preview.replace(/[*_=~#>`\[\]!]/g, '').slice(0, 90)
            : ((source.highlights || []).find(h => getHlStatus(h.ref) !== 'archiviert')?.text || '').replace(/[*_=~#>`\[\]!]/g, '').slice(0, 90);
          const catColor = `var(--color-cat-${source.category}, var(--color-text-tertiary))`;
          const emoji = source.is_custom ? '✏️' : source.category === 'books' ? '📖' : source.category === 'videos' ? '🎬' : source.category === 'podcasts' ? '🎙' : source.category === 'tweets' ? '💬' : '📄';
          return (
            <div key={idx} className={`source-item ${selectedSource === source ? 'active' : ''}${srcDone ? ' source-done' : ''}${kbSourceIdx === idx ? ' source-item-kb-focused' : ''}`} data-source-idx={idx} onClick={() => handleSelectSource(source)}>
              {/* Thumbnail / Icon */}
              <div className="source-item-thumb">
                {source.cover_image ? (
                  <img
                    src={source.cover_image} alt="" className="source-item-cover" loading="lazy"
                    onError={(e) => {
                      const img = e.target;
                      const retries = parseInt(img.dataset.retries || '0', 10);
                      if (retries < 2) {
                        // Retry nach Delay (CDN-Throttling nach Sync)
                        img.dataset.retries = String(retries + 1);
                        setTimeout(() => { img.src = source.cover_image; }, (retries + 1) * 2000);
                      } else {
                        img.style.display = 'none';
                        if (img.nextSibling) img.nextSibling.style.display = '';
                      }
                    }}
                  />
                ) : null}
                <span className={`source-item-icon ${source.category}`} style={source.cover_image ? { display: 'none' } : undefined}>{emoji}</span>
              </div>

              {/* Mitte: Titel, Autor, Datum, Badges */}
              <div className="source-item-body">
                <div className="source-item-title">{source.title || '(Ohne Titel)'}</div>
                <div className="source-item-meta">
                  {source.author && <span className="source-item-author">{source.author}</span>}
                  {(source.last_highlight_at || source.first_highlight_at || source.created_at) && (
                    <span className="source-item-date">
                      {new Date(source.last_highlight_at || source.first_highlight_at || source.created_at).toLocaleDateString('de-DE')}
                    </span>
                  )}
                </div>
                <div className="source-item-stats">
                  {srcTotal > 0 && (
                    <span className="source-stat">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                      {srcTotal}
                    </span>
                  )}
                  {linkedCount > 0 && (
                    <span className="source-stat source-stat-linked">
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 00-7.54-.54l-3 3a5 5 0 007.07 7.07l1.71-1.71"/></svg>
                      {linkedCount}
                    </span>
                  )}
                  {srcDone && <span className="source-stat source-stat-done">✓</span>}
                  {!srcDone && srcArchiviert > 0 && (
                    <span className="source-stat source-stat-progress">{srcOffen} offen</span>
                  )}
                </div>
              </div>

              {/* Rechts: Highlight-Vorschau mit farbigem Balken */}
              {previewText && (
                <div className="source-item-preview" style={{ '--cat-bar': catColor }}>
                  <span className="source-item-preview-text">{previewText}{previewText.length >= 90 ? '…' : ''}</span>
                </div>
              )}
            </div>
          );
        })}

        {/* Ghost Card entfernt – Formular ist jetzt oben in der Liste, Trigger in der Toolbar */}
      </div>

      {filtered.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--color-text-tertiary)', padding: 'var(--space-10)', fontSize: 'var(--text-sm)' }}>
          {sources.length === 0 ? 'Noch keine Readwise-Daten. Starte einen Sync!' : 'Keine Quellen in dieser Kategorie.'}
        </div>
      )}

      {/* Footer */}
      <div className="sidebar-footer">
        <span className="stats-pill">{filtered.length}{filtered.length !== sources.length ? ` / ${sources.length}` : ''} Quellen</span>
        {triageStats.archiviert > 0 && (
          <span className="stats-pill" style={{ color: 'var(--color-primary)' }}>
            {triageStats.archiviert} archiviert
          </span>
        )}
        <span className="stats-pill" style={{ marginLeft: 'auto', opacity: 0.4, fontSize: 'var(--text-xs)' }}>v{APP_VERSION}</span>
      </div>
    </div>
  </>);
}

export default React.memo(ReadwisePanel);
